import {Roles} from '@hconnect/apiclient'
import {DepartmentNode, UpmEntityType, TreeNode, EquipmentNode} from '@hconnect/common/types'
import {useUserContext} from '@hconnect/login'
import {dataTestId} from '@hconnect/uikit'
import {ChevronRight, ExpandMore, Restore as RestoreIcon} from '@mui/icons-material'
import {
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  alpha
} from '@mui/material'
import {styled} from '@mui/material/styles'
import {useCallback} from 'react'
import {NodeApi, NodeRendererProps} from 'react-arborist'

import {useItemDetails} from '../../providers'
import {PlantStructureActionButton} from '../PlantStructureActionButton'

const StyledListItem = styled(ListItem)(() => ({
  '&:hover .icon-button': {
    visibility: 'visible'
  },
  '.icon-button': {
    visibility: 'hidden'
  }
}))

const isDeletedNode = (node: TreeNode) => {
  return (
    (node.itemType === UpmEntityType.Department && node.isDeleted) ||
    (node.itemType === UpmEntityType.Equipment && node.isDeleted)
  )
}

const getLabel = (node: TreeNode) => node.text ?? ''

const getShouldHideArrow = <T extends TreeNode>(node: NodeApi<T>) => {
  return (
    node.isLeaf ||
    (node.data.children?.length ?? 0) === 0 ||
    node.data.itemType === UpmEntityType.Plant
  )
}

export const RestorePlantStructureNode = <T extends TreeNode>({
  node,
  style
}: NodeRendererProps<T>) => {
  const {loggedUser} = useUserContext()
  const {setEquipmentToRestore, setDepartmentToRestore} = useItemDetails()

  const userRoles = loggedUser?.hasRoles

  const canUpdateDepartments = !!userRoles && userRoles.includes(Roles.HproduceCCCSupporter)

  const canRestoreDepartments =
    node.data.itemType === UpmEntityType.Department && canUpdateDepartments

  const canRestoreEquipments =
    node.data.itemType === UpmEntityType.Equipment && canUpdateDepartments

  const shouldHideArrow = getShouldHideArrow(node)

  const handleExpandCollapseNode = useCallback(
    (event: React.MouseEvent) => {
      node.isInternal && node.toggle()
      event.stopPropagation()
    },
    [node]
  )

  const handleRestoreDepartmentClick = useCallback(
    (event: React.MouseEvent) => {
      setDepartmentToRestore(node.data as DepartmentNode)
      event.stopPropagation()
    },
    [node.data, setDepartmentToRestore]
  )

  const handleRestoreEquipmentClick = useCallback(
    (event: React.MouseEvent) => {
      setEquipmentToRestore(node.data as EquipmentNode)
      event.stopPropagation()
    },
    [node.data, setEquipmentToRestore]
  )

  const label = getLabel(node.data)

  return (
    <Stack>
      <StyledListItem dense disablePadding {...dataTestId(`list-item-${label}`)}>
        <ListItemButton
          style={style}
          sx={{
            backgroundColor: ({palette}) =>
              node.isSelected ? alpha(palette.primary.light, 0.1) : 'inherit',
            height: 56
          }}
        >
          <ListItemIcon
            sx={{
              display: shouldHideArrow ? 'none' : 'flex',
              minWidth: 30
            }}
            onClick={handleExpandCollapseNode}
          >
            {node.isOpen ? <ExpandMore color="primary" /> : <ChevronRight color="primary" />}
          </ListItemIcon>
          <ListItemText
            sx={{pl: shouldHideArrow ? 4 : 0}}
            primaryTypographyProps={{noWrap: true}}
            primary={label}
          />
          {isDeletedNode(node.data) && (
            <PlantStructureActionButton
              shouldBeDisplayed={canRestoreDepartments}
              testId={`restore-departments-${node.data.upmId}`}
              icon={<RestoreIcon />}
              handleEvent={handleRestoreDepartmentClick}
            />
          )}
          {isDeletedNode(node.data) && (
            <PlantStructureActionButton
              shouldBeDisplayed={canRestoreEquipments}
              testId={`restore-equipments-${node.data.upmId}`}
              icon={<RestoreIcon />}
              handleEvent={handleRestoreEquipmentClick}
            />
          )}
        </ListItemButton>
      </StyledListItem>
      <Divider variant="middle" sx={{mx: 4}} />
    </Stack>
  )
}
